import classNames from 'classnames';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { AsyncLoadStatus } from '../../common/types';
import useFormatter from '../../common/useFormatter';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import * as Icon from '../../components/icons';
import { InputSearch } from '../../components/inputs/InputSearch';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import TitleSection from '../../components/layout/TitleSection';
import { ActionBar } from '../../components/nav/ActionBar';
import { KPI_TAG_METADATA } from '../../dashboard/kpi/kpiModel';
import {
  userSettings,
  userSettingsLoadStatus,
} from '../../store/warehouse.state';
import { UserExtraSettings } from '../../store/warehouse.types';
import useUpdateUserSettings from '../../warehouse/hooks/useUpdateUserSettings';
import { actualityAggregatedStatsUserSelectedMetrics } from '../store/actuality.kpi.state';
import { ActualityAggregatedHqExtendedStatsDataColumn } from '../store/actuality.kpi.types';
import { getActualityAggregationMetricDescriptors } from '../store/feedMetrics';

const StatManagementComponent = () => {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const callUpdate = useUpdateUserSettings();
  const userSettingsData = useRecoilValue(userSettings);
  const updateStatus = useRecoilValue(userSettingsLoadStatus);
  const selectedMetrics = useRecoilValue(
    actualityAggregatedStatsUserSelectedMetrics,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const metricDescriptorsAll = getActualityAggregationMetricDescriptors(
    t,
    formatter,
  );

  const selectedSet = new Set(selectedMetrics);

  const handleToggle = (id: ActualityAggregatedHqExtendedStatsDataColumn) => {
    const userExtraSettings: UserExtraSettings = {
      ...userSettingsData?.extraSettings,
      actualitySettings: {
        ...userSettingsData?.extraSettings?.actualitySettings,
        aggregatedMetrics: selectedSet.has(id)
          ? _.filter(selectedMetrics, s => s !== id)
          : [...selectedMetrics, id],
      },
    };

    callUpdate({
      extraSettings: userExtraSettings,
    });
  };

  // Filter and group stats by category
  const groupedStats = useMemo(() => {
    const filtered = searchTerm
      ? metricDescriptorsAll.filter(item =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : metricDescriptorsAll;

    return _.groupBy(filtered, 'category');
  }, [metricDescriptorsAll, searchTerm]);

  return (
    <>
      <ScreenTitle
        title={t`KPI Visibility`}
        subtitle={t`Feed`}
        helpNavTo={'actuality/actuality-kpi'}
        icon={Icon.ObjectSettings}
        isSticky
        className="top-20"
      />

      <div className="mb-6">
        <ActionBar className="p-2">
          <InputSearch
            placeholder={t('Search KPIs...')}
            value={searchTerm}
            onChange={(value: string) => setSearchTerm(value)}
            panelTitleMode
            classNamePlaceholder="!left-2 !top-2"
            classNameInput="!px-2 !py-1"
          />
        </ActionBar>

        {_.map(groupedStats, (metricDescriptor, groupKey) => (
          <TitleSection
            key={groupKey}
            title={KPI_TAG_METADATA[groupKey]?.title || groupKey}
            inPanelView
            classNameInner="p-4"
          >
            <ul className="space-y-4">
              {metricDescriptor.map(({ id, title }) => {
                const isDisabled = !selectedSet.has(id);
                return (
                  <li
                    className={classNames('flex items-start')}
                    key={`${groupKey}-${id}`}
                  >
                    <label
                      className={classNames(
                        'mr-1 flex-1 truncate',
                        isDisabled ? 'opacity-50' : 'opacity-80',
                      )}
                    >
                      {title}
                    </label>
                    <ButtonSwitchMulti
                      autoSize
                      options={[{ label: t('off') }, { label: t('on') }]}
                      selectedIndex={isDisabled ? 0 : 1}
                      buttonType={isDisabled ? 'minimal' : 'secondary'}
                      disabled={updateStatus !== AsyncLoadStatus.Ok}
                      onClick={() => handleToggle(id)}
                    />
                  </li>
                );
              })}
            </ul>
          </TitleSection>
        ))}
      </div>
    </>
  );
};

export default StatManagementComponent;
